import { Box, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import type { DragEvent } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DragPayload } from '@/features/bidding/types/bidLadderInput'

type UnassignDropZoneProps = {
  /** Drag payload from the bid ladder - controls visibility of the drop zone */
  ladderDragPayload: DragPayload | null
  /** Whether there are any assigned groups (drop zone only shows when true) */
  hasAssignedGroups: boolean
  /** Function to unassign a regulation object from a bid level */
  unassignRegulationObjectGroup: (levelId: string, regulationObjectCode: string) => void
  /** Called when the drop operation completes */
  onDropComplete: () => void
}

function UnassignDropZone({
  ladderDragPayload,
  hasAssignedGroups,
  unassignRegulationObjectGroup,
  onDropComplete,
}: Readonly<UnassignDropZoneProps>) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState(false)

  const isDraggingFromLadder = ladderDragPayload !== null && hasAssignedGroups

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setIsHovered(true)
  }, [])

  const handleDragLeave = useCallback((event: DragEvent) => {
    // Only reset if leaving the drop zone entirely
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsHovered(false)
    }
  }, [])

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      setIsHovered(false)

      try {
        const data = event.dataTransfer.getData('application/json')
        const payload = JSON.parse(data) as DragPayload

        // Only handle drops from bid ladder (has sourceLevelId)
        if (payload.sourceLevelId) {
          const sourceLevelId = payload.sourceLevelId
          // Unassign all regulation objects in the payload
          payload.regulationObjectCodes.forEach((code) => {
            unassignRegulationObjectGroup(sourceLevelId, code)
          })
          onDropComplete()
        }
      } catch (error) {
        console.error('Failed to parse drag data:', error)
      }
    },
    [unassignRegulationObjectGroup, onDropComplete],
  )

  if (!isDraggingFromLadder) {
    return null
  }

  const borderColor = isHovered ? 'primary.main' : 'primary.light'
  const backgroundColor = isHovered ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.04)

  return (
    <Box
      data-testid="unassign-drop-zone"
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1,
        border: '2px dashed',
        borderColor,
        backgroundColor,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 56,
        animation: 'pulse 2s ease-in-out infinite',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 0.7 },
          '50%': { opacity: 1 },
        },
      }}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: isHovered ? 'primary.main' : 'primary.light',
          userSelect: 'none',
          lineHeight: 1,
        }}
        variant="caption"
      >
        {t('bidding.bidding_cascading.groups_tab.drop_to_unassign')}
      </Typography>
    </Box>
  )
}

export default UnassignDropZone
